"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { authClient } from "@/lib/auth-client";
import Link from "next/link";
import { Spinner } from "@/components/ui/spinner";
import { IconCheck } from "@tabler/icons-react";
import { toast } from "sonner";
import { AuthCardShell } from "./auth-card-shell";

export default function ForgotPasswordAuth() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authClient.requestPasswordReset({
        email,
        redirectTo: `${process.env.NEXT_PUBLIC_APP_URL || "http://localhost:3000"}/reset-password`,
      });
      setSuccess(true);
    } catch (err) {
      console.error("Password reset request failed:", err);
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCardShell
      title="Reset your password"
      description="Enter your email address and we'll send you a link to reset your password"
      footer={
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Remember your password?{" "}
          <Link
            href="/sign-in"
            className="underline underline-offset-4 hover:text-primary"
          >
            Sign in
          </Link>
        </p>
      }
    >
      {success ? (
        <div className="rounded-md border border-green-500/50 bg-green-500/10 p-4">
          <div className="flex items-start gap-3">
            <IconCheck className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
            <div className="flex-1 text-left">
              <h3 className="font-semibold text-green-600 dark:text-green-500">
                Check your email
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                We&apos;ve sent a password reset link to <strong>{email}</strong>.
                The link will expire in 1 hour.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Didn&apos;t receive the email? Check your spam folder or{" "}
                <button
                  type="button"
                  onClick={() => {
                    setSuccess(false);
                    setEmail("");
                  }}
                  className="text-green-600 dark:text-green-500 underline hover:no-underline"
                >
                  try again
                </button>
                .
              </p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                required
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                disabled={loading}
                autoComplete="email"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full justify-center">
              {loading && <Spinner className="mr-2 size-4" aria-hidden="true" />}
              {loading ? "Sending reset link..." : "Send reset link"}
            </Button>
          </div>
        </form>
      )}
    </AuthCardShell>
  );
}
